import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import BrochureConfirmModal from '../../components/BrochureConfirmModal/BrochureConfirmModal';
import { getRequestedLanguages } from '../../locale/languages';
import {
    BrochureProduct,
    BrochureTemplate,
    BrochureDetail,
    BrochureSelectableProduct,
    getBrochureBasketTemplates,
    createBrochureInBasket,
    updateBrochureInBasket,
    deleteBrochureFromBasket,
    getBrochureSelectableProducts,
    BROCHURE_BASKETS_BASE,
} from '../../api/brochures';
import { Route } from './types';
import MessageToastComponent from '../../components/MessageToastComponent/MessageToastComponent';
import { SelectedCustomerData } from '../../utils/models/cart';
import { format } from 'date-fns';

interface BrochureSlot {
    productId: string | null;
    withCard: boolean;
    format: string;
}

interface BrochurePage {
    id: number;
    templateValue: string; // stores template id as string
    title: string;
    products: BrochureSlot[];
}

const buildInitialProducts = (maxProducts: number): BrochureSlot[] =>
    Array(maxProducts)
        .fill(null)
        .map(() => ({ productId: null, withCard: false, format: '' }));

interface PageCardProps {
    page: BrochurePage;
    pageNumber: number;
    isFirst: boolean;
    isLast: boolean;
    language: any;
    products: BrochureSelectableProduct[];
    templates: BrochureTemplate[];
    validationTrigger: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDelete: () => void;
    onUpdate: (updated: BrochurePage) => void;
}

const PageCard: FC<PageCardProps> = ({
    page,
    pageNumber,
    isFirst,
    isLast,
    language,
    products,
    templates,
    validationTrigger,
    onMoveUp,
    onMoveDown,
    onDelete,
    onUpdate,
}) => {
    const cfg = language.brochures.configurator;
    const configuredLabel = language.brochures.detail.configured;

    const currentTemplate = templates.find((t) => String(t.id) === page.templateValue) ?? templates[0];
    const slotCount = currentTemplate?.max_products ?? 0;

    const [touchedSlots, setTouchedSlots] = useState<Set<number>>(new Set());

    useEffect(() => {
        setTouchedSlots(new Set());
    }, [validationTrigger]);

    const handleTemplateChange = (value: string) => {
        const tpl = templates.find((t) => String(t.id) === value);
        if (!tpl) return;
        const newProducts: BrochureSlot[] = Array(tpl.max_products)
            .fill(null)
            .map((_, i) => page.products[i] ?? { productId: null, withCard: false, format: '' });
        onUpdate({ ...page, templateValue: value, products: newProducts });
    };

    const handleProductChange = (slotIndex: number, productId: string) => {
        const currentProduct = page.products[slotIndex];
        const newProducts = [...page.products];
        newProducts[slotIndex] = {
            ...newProducts[slotIndex],
            productId: productId || null,
            format: productId ? currentProduct?.format || 'medium' : '',
        };
        setTouchedSlots((prev) => new Set(prev).add(slotIndex));
        onUpdate({ ...page, products: newProducts });
    };

    const handleCardChange = (slotIndex: number, withCard: boolean) => {
        const newProducts = [...page.products];
        newProducts[slotIndex] = { ...newProducts[slotIndex], withCard };
        setTouchedSlots((prev) => new Set(prev).add(slotIndex));
        onUpdate({ ...page, products: newProducts });
    };

    const handleFormatChange = (slotIndex: number, format: string) => {
        const newProducts = [...page.products];
        newProducts[slotIndex] = { ...newProducts[slotIndex], format };
        setTouchedSlots((prev) => new Set(prev).add(slotIndex));
        onUpdate({ ...page, products: newProducts });
    };

    const handleTitleChange = (value: string) => {
        onUpdate({ ...page, title: value });
    };

    const pageLabel = `${cfg.page} ${String(pageNumber).padStart(2, '0')}`;

    return (
        <div className="brochureConfiguratorPageCard" data-page-index={pageNumber - 1}>
            <div className="brochureConfiguratorPageHeader">
                <div className="brochureConfiguratorPageHeaderLeft">
                    <i className="svgfont-document"></i>
                    <span className="brochureConfiguratorPageTitle">{pageLabel}</span>
                </div>
                <div className="brochureConfiguratorPageHeaderActions">
                    {!isFirst && (
                        <button className="brochureConfiguratorPageAction" onClick={onMoveUp}>
                            {cfg.move_up} <i className="svgfont-new-arrow-up"></i>
                        </button>
                    )}
                    {!isLast && (
                        <button className="brochureConfiguratorPageAction" onClick={onMoveDown}>
                            {cfg.move_down} <i className="svgfont-new-arrow-down"></i>
                        </button>
                    )}
                    <button className="brochureConfiguratorPageAction danger" onClick={onDelete}>
                        {cfg.delete_page} <i className="icon errea-trash"></i>
                    </button>
                </div>
            </div>

            <div className="brochureConfiguratorPageBody">
                <div className="brochureConfiguratorPageLeft">
                    <div className="brochureConfiguratorSection">
                        <span className="brochureConfiguratorSectionTitle">{cfg.graphic_template}</span>
                        <div className="brochureConfiguratorField">
                            <label style={{ display: 'flex' }}>{cfg.select_template}</label>
                            <div className="brochureConfiguratorSelectWrapper">
                                <select
                                    value={page.templateValue}
                                    onChange={(e) => handleTemplateChange(e.target.value)}
                                    className="brochureConfiguratorSelect"
                                >
                                    {templates.map((t) => (
                                        <option key={t.id} value={String(t.id)}>
                                            {t.slug}
                                        </option>
                                    ))}
                                </select>
                                <i className="svgfont-chevron-down brochureConfiguratorSelectArrow"></i>
                            </div>
                        </div>

                        {currentTemplate?.preview_image && (
                            <img
                                className="brochureConfiguratorTemplatePreview"
                                src={currentTemplate.preview_image}
                                alt={currentTemplate.slug}
                            />
                        )}
                    </div>

                    <div className="brochureConfiguratorSection">
                        <span className="brochureConfiguratorSectionTitle">{cfg.header}</span>
                        <div className="brochureConfiguratorField">
                            <label style={{ display: 'flex' }}>{cfg.page_title}</label>
                            <input
                                type="text"
                                className="brochureConfiguratorInput"
                                value={page.title}
                                onChange={(e) => handleTitleChange(e.target.value)}
                                style={!page.title.trim() ? { borderColor: '#c0392b' } : undefined}
                            />
                        </div>
                    </div>
                </div>

                <div className="brochureConfiguratorPageRight">
                    <div className="brochureConfiguratorSection">
                        <span className="brochureConfiguratorSectionTitle">{cfg.products}</span>
                        <label className="brochureConfiguratorProductsLabel">{cfg.select_products}</label>
                        <div
                            className="brochureConfiguratorProductsList"
                            style={
                                !page.products
                                    .slice(0, slotCount)
                                    .some((s) => s.productId !== null && s.productId !== '')
                                    ? { border: '1px solid #c0392b', borderRadius: '4px', padding: '4px' }
                                    : undefined
                            }
                        >
                            {Array.from({ length: slotCount }, (_, i) => {
                                const slot: BrochureSlot = page.products[i] ?? {
                                    productId: null,
                                    withCard: false,
                                    format: '',
                                };
                                const selectedProduct = products.find((p) => String(p.id) === slot.productId);
                                const canHaveCard = !!selectedProduct?.isCustomProduct;
                                const isSlotInvalid =
                                    validationTrigger > 0 &&
                                    !touchedSlots.has(i) &&
                                    slot.productId &&
                                    slot.format === '';
                                return (
                                    <div
                                        key={i}
                                        className={`brochureConfiguratorProductRow${isSlotInvalid ? ' brochureConfiguratorProductRow--error' : ''}`}
                                    >
                                        <span className="brochureConfiguratorProductIndex">#{i + 1}</span>
                                        <div className="brochureConfiguratorProductThumb">
                                            {selectedProduct ? (
                                                selectedProduct.image ? (
                                                    <img src={selectedProduct.image} alt={selectedProduct.name} />
                                                ) : (
                                                    <div className="brochureConfiguratorProductThumbFilled" />
                                                )
                                            ) : (
                                                <div className="brochureConfiguratorProductThumbEmpty">
                                                    <i className="icon errea-shirt"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="brochureConfiguratorSelectWrapper brochureConfiguratorProductSelect">
                                            <select
                                                value={slot.productId ?? ''}
                                                onChange={(e) => handleProductChange(i, e.target.value)}
                                                className="brochureConfiguratorSelect"
                                            >
                                                <option value="">{cfg.select_product_placeholder}</option>
                                                {products.map((p) => (
                                                    <option key={p.id} value={String(p.id)}>
                                                        {p.isCustomProduct
                                                            ? `${p.name} (${configuredLabel} - ${p.customProduct?.id})`
                                                            : p.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <i className="svgfont-chevron-down brochureConfiguratorSelectArrow"></i>
                                        </div>
                                        <div className="brochureConfiguratorSelectWrapper brochureConfiguratorFormatSelect">
                                            <select
                                                value={slot.productId ? slot.format : ''}
                                                onChange={(e) => handleFormatChange(i, e.target.value)}
                                                className="brochureConfiguratorSelect"
                                                disabled={!slot.productId}
                                            >
                                                <option value="" disabled>
                                                    {cfg.format_placeholder}
                                                </option>
                                                <option value="small">{cfg.format_small}</option>
                                                <option value="medium">{cfg.format_normal}</option>
                                                <option value="large">{cfg.format_large}</option>
                                            </select>
                                            <i className="svgfont-chevron-down brochureConfiguratorSelectArrow"></i>
                                        </div>
                                        <label
                                            className={`brochureConfiguratorCardLabel${!canHaveCard ? ' brochureConfiguratorCardLabel--disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={slot.withCard && !!slot.format && canHaveCard}
                                                onChange={(e) => handleCardChange(i, e.target.checked)}
                                                disabled={!slot.format || !slot.productId}
                                            />
                                            <span>{cfg.card}</span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface Props {
    basketId: string;
    brochureId: string;
    brochureData: BrochureDetail | null;
    products: BrochureProduct[];
    raccoltaBrochure: string;
    company: string;
    onLoadingChange: (loading: boolean) => void;
    onNavigate: (route: Route) => void;
}

const BrochureConfiguratorView: FC<Props> = ({
    basketId,
    brochureId: initialBrochureId,
    brochureData,
    products,
    raccoltaBrochure,
    company,
    onLoadingChange,
    onNavigate,
}) => {
    const language = getRequestedLanguages(window.errea_accountUserData.language);
    const cfg = language.brochures.configurator;
    const selectableProducts = getBrochureSelectableProducts(products);

    useEffect(() => {
        if (products.length === 0) {
            onNavigate({ view: 'detail', id: basketId });
        }
    }, []);

    const [pages, setPages] = useState<BrochurePage[]>([]);
    const [nextPageId, setNextPageId] = useState(1);
    const [templates, setTemplates] = useState<BrochureTemplate[]>([]);
    const [saveError, setSaveError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [brochureChanged, setBrochureChanged] = useState(false);
    const [showBackModal, setShowBackModal] = useState(false);
    const [validationTrigger, setValidationTrigger] = useState(0);
    const [brochureId, setBrochureId] = useState(initialBrochureId);

    const isNew = useMemo(() => brochureId === 'new', [brochureId]);
    const [brochureName, setBrochureName] = useState(isNew ? '' : (brochureData?.name ?? ''));

    useEffect(() => {
        setBrochureId(initialBrochureId);
    }, [initialBrochureId]);

    useEffect(() => {
        onLoadingChange(true);
        getBrochureBasketTemplates()
            .then((result) => setTemplates(result))
            .catch(() => {})
            .finally(() => onLoadingChange(false));
    }, []);

    useEffect(() => {
        if (!brochureChanged) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [brochureChanged]);

    useEffect(() => {
        if (isNew || !brochureData || templates.length === 0) return;

        setBrochureName(brochureData.name);
        setPages(
            brochureData.pages.map((page) => {
                const template = templates.find((t) => t.id === page.template.id);
                const slotCount = template?.max_products ?? page.products.length;
                const slots: BrochureSlot[] = Array.from({ length: slotCount }, () => ({
                    productId: null,
                    withCard: false,
                    format: '',
                }));
                page.products.forEach((p) => {
                    const slotIndex = p.position - 1;
                    if (slotIndex < 0 || slotIndex >= slotCount) return;
                    slots[slotIndex] = {
                        productId: String(p.custom_product?.id ?? p.id),
                        withCard: p.with_card_side,
                        format: p.image_preset ?? '',
                    };
                });
                return {
                    id: page.id,
                    templateValue: String(page.template.id),
                    title: page.title,
                    products: slots,
                };
            }),
        );
        setNextPageId(Math.max(...brochureData.pages.map((p) => p.id)) + 1);
    }, [brochureData, templates]);

    const movePage = (index: number, direction: 'up' | 'down') => {
        const newPages = [...pages];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newPages[index], newPages[swapIndex]] = [newPages[swapIndex], newPages[index]];
        setPages(newPages);
        setBrochureChanged(true);
    };

    const deletePage = (index: number) => {
        setPages(pages.filter((_, i) => i !== index));
        setBrochureChanged(true);
    };

    const updatePage = (index: number, updated: BrochurePage) => {
        const newPages = [...pages];
        newPages[index] = updated;
        setPages(newPages);
        setBrochureChanged(true);
    };

    const addPage = () => {
        const defaultTemplate = templates[0];
        if (!defaultTemplate) return;
        setPages([
            ...pages,
            {
                id: nextPageId,
                templateValue: String(defaultTemplate.id),
                title: '',
                products: buildInitialProducts(defaultTemplate.max_products),
            },
        ]);
        setNextPageId(nextPageId + 1);
        setBrochureChanged(true);
    };

    const handleDeleteBrochure = () => {
        setDeleting(true);
        setShowDeleteModal(false);
        deleteBrochureFromBasket(basketId, brochureId)
            .then(() => onNavigate({ view: 'detail', id: basketId }))
            .catch(() => {
                setSaveError(cfg.errors.error_deleting);
                setDeleting(false);
            });
    };

    const saveBrochure = () => {
        if (isBrochureInvalid) {
            setSaveError(cfg.errors.validation_error);
            setValidationTrigger((prev) => prev + 1);
            scrollToFirstError();
            return;
        }
        onLoadingChange(true);
        const payload = {
            name: brochureName,
            pages: pages.map((page) => ({
                template_id: Number(page.templateValue ?? page.id),
                title: page.title,
                products: page.products.flatMap((slot, index) => {
                    if (!slot.productId) return [];
                    const product = selectableProducts.find((p) => p.id === slot.productId);
                    if (!product) return [];
                    return [
                        {
                            position: index + 1,
                            custom_product_id: product.isCustomProduct ? product.entityId : undefined,
                            image_preset: slot.format || undefined,
                            id: product.baseProductId,
                            with_card_side: slot.withCard,
                        },
                    ];
                }),
            })),
        };
        const request = isNew
            ? createBrochureInBasket(basketId, payload)
            : updateBrochureInBasket(basketId, brochureId, payload);

        request
            .then((newBrochure) => {
                if (isNew) {
                    setBrochureId(`${newBrochure.id}`);
                    onNavigate({ view: 'configurator', id: `${newBrochure.id}` });
                }
            })
            .catch((err) => setSaveError(typeof err === 'string' ? err : cfg.errors.error_creating))
            .finally(() => {
                onLoadingChange(false);
                setBrochureChanged(false);
            });
    };

    const buildDownloadLink = useCallback(
        (downloadPath: string) => {
            const customerDataItem = localStorage.getItem('errea_selectedCustomer');
            if (!customerDataItem) {
                setSaveError(cfg.errors.missing_customer);
                return '';
            }

            const customerData = JSON.parse(customerDataItem) as SelectedCustomerData;

            const downloadUrl = new URL(
                `${BROCHURE_BASKETS_BASE}/brochure-baskets/${basketId}/brochures/${brochureId}/${downloadPath}`,
                window.location.origin,
            );
            downloadUrl.searchParams.append('logged_in_customer_id', customerData.customerNo);

            return downloadUrl.toString();
        },
        [BROCHURE_BASKETS_BASE, basketId, brochureId],
    );

    const pdfUrlRequest = buildDownloadLink('download-pdf');
    const imagesUrlRequest = buildDownloadLink('download-zip');

    const handleDownloadLink = (downloadUrl: string, filename: string, extension: string) => {
        onLoadingChange(true);

        fetch(downloadUrl)
            .then((res) => {
                if (!res.ok) throw new Error('Download failed');
                return res.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${filename || brochureName || 'brochure'}.${extension}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                setSaveError(cfg.errors.error_downloading);
            })
            .finally(() => onLoadingChange(false));
    };

    const hasCustomProducts = useMemo(() => {
        const customProducts = selectableProducts.filter(({ isCustomProduct }) => isCustomProduct);
        const pageProducts = pages.flatMap((page) => page.products);

        const hasCustomProducts = pageProducts.some(({ productId }) => {
            if (!productId) {
                return false;
            }

            return customProducts.some(({ id }) => id === productId);
        });

        return hasCustomProducts;
    }, [selectableProducts, pages]);

    const isBrochureInvalid = useMemo(() => {
        if (!brochureName.trim() || pages.length === 0) {
            return true;
        }

        const invalidPage = pages.find((page) => {
            if (!page.title.trim()) {
                return true;
            }

            const template = templates.find((t) => String(t.id) === page.templateValue);
            if (!template) {
                return true;
            }

            const validProductCount = page.products.filter((slot) => slot.productId).length;
            if (validProductCount === 0 || validProductCount > template.max_products) {
                return true;
            }

            const invalidProduct = page.products.find((slot) => slot.productId && slot.format === '');
            if (invalidProduct) {
                return true;
            }
        });

        if (invalidPage) {
            return true;
        }

        return false;
    }, [brochureName, pages, templates]);

    const pagesContainerRef = useRef<HTMLDivElement>(null);

    const getFirstInvalidPageIndex = useCallback((): number => {
        if (!brochureName.trim() || pages.length === 0) return -1;
        return pages.findIndex((page) => {
            if (!page.title.trim()) return true;
            const template = templates.find((t) => String(t.id) === page.templateValue);
            if (!template) return true;
            const validProductCount = page.products.filter((slot) => slot.productId).length;
            if (validProductCount === 0 || validProductCount > template.max_products) return true;
            return page.products.some((slot) => slot.productId && slot.format === '');
        });
    }, [brochureName, pages, templates]);

    const scrollToFirstError = useCallback(() => {
        const idx = getFirstInvalidPageIndex();
        if (idx >= 0 && pagesContainerRef.current) {
            const card = pagesContainerRef.current.querySelector(`[data-page-index="${idx}"]`);
            if (card) {
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [getFirstInvalidPageIndex]);

    return (
        <div className="brochureConfigurator">
            <MessageToastComponent message={saveError} type={'error'} onHide={() => setSaveError('')} />

            <a
                href="#"
                className="brochureDetailBack"
                onClick={(e) => {
                    e.preventDefault();
                    if (brochureChanged) {
                        setShowBackModal(true);
                    } else {
                        onNavigate({ view: 'detail', id: basketId });
                    }
                }}
            >
                <i className="icon errea-arrow-left"></i>
                <span>{language.brochures.detail.go_back}</span>
            </a>

            <div className="brochureConfiguratorHeader">
                <div className="brochureConfiguratorNameRow">
                    <div className="brochureConfiguratorNameField">
                        <label className="brochureConfiguratorNameLabel" style={{ display: 'flex' }}>
                            {cfg.brochure_name}
                        </label>
                        <input
                            type="text"
                            className="brochureConfiguratorNameInput"
                            value={brochureName}
                            onChange={(e) => {
                                setBrochureName(e.target.value);
                                setBrochureChanged(true);
                            }}
                            style={!brochureName.trim() ? { borderColor: '#c0392b' } : undefined}
                        />
                    </div>
                    {!isNew && (
                        <button
                            className={`brochureConfiguratorDeleteBtn${deleting ? ' brochureConfiguratorDeleteBtn--disabled' : ''}`}
                            onClick={!deleting ? () => setShowDeleteModal(true) : undefined}
                        >
                            <span>{cfg.delete_brochure}</span>
                            <i className="icon errea-trash"></i>
                        </button>
                    )}
                </div>
                <div className="brochureConfiguratorMetaRow">
                    <div className="brochureConfiguratorMetaItem">
                        <span className="brochureConfiguratorMetaLabel">
                            {language.brochures.list.collection_label}
                        </span>
                        <span className="brochureConfiguratorMetaValue">{raccoltaBrochure}</span>
                    </div>
                    <div className="brochureConfiguratorMetaItem">
                        <span className="brochureConfiguratorMetaLabel">{language.brochures.list.company_label}</span>
                        <span className="brochureConfiguratorMetaValue">{company}</span>
                    </div>
                    <div className="brochureConfiguratorMetaItem">
                        <span className="brochureConfiguratorMetaLabel">{language.brochures.detail.last_modified}</span>
                        <span className="brochureConfiguratorMetaValue">
                            {brochureData?.updated_at
                                ? format(new Date(brochureData.updated_at), language.general.date_format)
                                : '-'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="brochureConfiguratorContent">
                <div className="brochureConfiguratorPages" ref={pagesContainerRef}>
                    {pages.length > 0 &&
                        pages.map((page, index) => (
                            <PageCard
                                key={page.id}
                                page={page}
                                pageNumber={index + 1}
                                isFirst={index === 0}
                                isLast={index === pages.length - 1}
                                language={language}
                                products={selectableProducts}
                                templates={templates}
                                validationTrigger={validationTrigger}
                                onMoveUp={() => movePage(index, 'up')}
                                onMoveDown={() => movePage(index, 'down')}
                                onDelete={() => deletePage(index)}
                                onUpdate={(updated) => updatePage(index, updated)}
                            />
                        ))}

                    <div className="brochureConfiguratorAddPage">
                        <ButtonComponent style="outline" text={cfg.add_page} icon_left="add" onClick={addPage} />
                    </div>
                </div>

                <div className="brochureConfiguratorSidebar">
                    <ButtonComponent style="solid" text={cfg.save_brochure} icon_right="check" onClick={saveBrochure} />
                    <ButtonComponent
                        style="solid"
                        text={cfg.download_pdf}
                        icon_right="download"
                        onClick={() => handleDownloadLink(pdfUrlRequest, `${brochureName || 'brochure'}`, 'pdf')}
                        disabled={!pdfUrlRequest || isBrochureInvalid || brochureChanged}
                    />
                    <ButtonComponent
                        style="solid"
                        text={cfg.download_images}
                        icon_right="download"
                        onClick={() => handleDownloadLink(imagesUrlRequest, `${brochureName || 'brochure'}`, 'zip')}
                        disabled={!imagesUrlRequest || isBrochureInvalid || brochureChanged || !hasCustomProducts}
                    />
                    <ButtonComponent
                        style="solid"
                        text={cfg.create_order}
                        icon_right="order"
                        onClick={() => onNavigate({ view: 'create_order', id: brochureId })}
                        disabled={!hasCustomProducts || brochureChanged}
                    />
                </div>
            </div>
            {showDeleteModal && (
                <BrochureConfirmModal
                    text={cfg.delete_brochure_confirm.replace('{{name}}', brochureName)}
                    onConfirm={handleDeleteBrochure}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
            {showBackModal && (
                <BrochureConfirmModal
                    text={cfg.unsaved_changes_warning}
                    confirmLabel={language.modal.leave}
                    onConfirm={() => {
                        setShowBackModal(false);
                        onNavigate({ view: 'detail', id: basketId });
                    }}
                    onCancel={() => setShowBackModal(false)}
                />
            )}
        </div>
    );
};

export default BrochureConfiguratorView;
